import React from 'react';
import Link from 'next/link';
import styled from 'styled-components';
import { Box, Heading } from '@99designs/emu';
import { FormattedMessage } from '@99designs/frontend-components/i18n';
import { royalBlue } from '../../../utils/customColors';
import { highlightedTextStyles, highlightedTextUnderlineStyles } from '../styles';
import type { StepSummary } from '../createStepSummaries';

const StyledHeading = styled(Heading)<{ hidden: boolean }>`
    transition: 0.5s;
    opacity: ${({ hidden }) => (hidden ? '0' : '0.7')};
`;

const HighlightedTextUnderline = styled.span`
    ${highlightedTextUnderlineStyles}
    color: ${royalBlue};
`;

const AnswerLink = styled(Link)`
    ${highlightedTextStyles}
`;

const OverlayWithOpacity = styled.div`
    position: relative;
    &:after {
        content: ' ';
        z-index: 10;
        display: block;
        position: absolute;
        height: 100%;
        top: 0;
        left: 0;
        right: 0;
        pointer-events: none;
        background: linear-gradient(white, transparent);
        background: -webkit-linear-gradient(
            top,
            rgba(255, 255, 255, 1) 0%,
            rgba(255, 255, 255, 0) 100%
        );
    }
`;

export const SummaryAnimation = styled.div<{
    state: string;
    currentIndex: number;
    nextStepIndex: number;
    previousStepIndex: number;
    skipAnimation: boolean;
}>`
    transition: 0.5s;
    transition-delay: 0;

    ${({ state, currentIndex, nextStepIndex, previousStepIndex, skipAnimation }) => {
        const isNavigatingForward = nextStepIndex > currentIndex;
        const isNavigatingOutOfStep = nextStepIndex === -1;
        const isNotFromStep = previousStepIndex === -1;
        const isFromPreviousStep = previousStepIndex < currentIndex;

        switch (state) {
            case 'from':
                if (skipAnimation) {
                    return;
                }
                if (isNotFromStep) {
                    return 'opacity: 0; transform: translateY(28px);';
                }
                return isFromPreviousStep
                    ? 'transform: translateY(28px);'
                    : 'transform: translateY(0px);';
            case 'enter':
                return 'transform: translateY(0); opacity: 1;';
            case 'leave':
                if (isNavigatingForward || skipAnimation) {
                    return;
                }
                if (isNavigatingOutOfStep) {
                    return 'transform: translateY(28px); opacity: 0;';
                }
                return 'transform: translateY(28px);';
        }
    }}
`;

interface StepSummaryNavProps {
    summaries: StepSummary[];
    currentStepIndex: number;
    nextStepIndex: number;
    previousStepIndex: number;
    transitionState: string;
}

export function StepSummaryNav({
    summaries,
    currentStepIndex,
    nextStepIndex,
    previousStepIndex,
    transitionState,
}: StepSummaryNavProps) {
    const skipAnimation = summaries[previousStepIndex]
        ? summaries[previousStepIndex].hideIfEmpty
        : false;
    const stepSummaries = summaries.slice(0, currentStepIndex);

    return (
        <OverlayWithOpacity>
            <SummaryAnimation
                state={transitionState}
                currentIndex={currentStepIndex}
                nextStepIndex={nextStepIndex}
                previousStepIndex={previousStepIndex}
                skipAnimation={skipAnimation}
            >
                <Box
                    display="inline-flex"
                    flexDirection="column"
                    alignItems="flex-end"
                    marginBottom="3bu"
                    height="120px"
                >
                    <Box marginTop="auto">
                        {/* eslint-disable-next-line array-callback-return */}
                        {stepSummaries.map((stepSummary, index) => {
                            if (!stepSummary.hideIfEmpty) {
                                return (
                                    <StyledHeading
                                        appearanceLevel={4}
                                        accessibilityLevel={3}
                                        key={stepSummary.link}
                                        hidden={
                                            // eslint-disable-next-line eqeqeq
                                            transitionState == 'leave' && index >= nextStepIndex
                                        }
                                    >
                                        <FormattedMessage
                                            message={stepSummary.question}
                                            values={{
                                                answer: (
                                                    <HighlightedTextUnderline>
                                                        <AnswerLink href={stepSummary.link}>
                                                            {stepSummary.answer}
                                                        </AnswerLink>
                                                    </HighlightedTextUnderline>
                                                ),
                                            }}
                                        />
                                    </StyledHeading>
                                );
                            }
                        })}
                    </Box>
                </Box>
            </SummaryAnimation>
        </OverlayWithOpacity>
    );
}
